import type { RunSettingsOverride } from '@af/shared';
import { Popover } from 'radix-ui';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/lib/permissions';
import { cn } from '@/lib/utils';

type Toggle = 'plan' | 'research' | 'auto';

/** Explicit on/off per toggle; `undefined` = the agent's own setting. */
export function toggleState(value: RunSettingsOverride, toggle: Toggle): boolean | undefined {
  if (toggle === 'plan') return value.planReview === undefined ? undefined : value.planReview !== 'off';
  if (toggle === 'research') return value.webResearch;
  return value.executionApproval === undefined ? undefined : value.executionApproval === 'auto';
}

/** Flip one toggle; the others are left exactly as they were. */
export function flipToggle(value: RunSettingsOverride, toggle: Toggle): RunSettingsOverride {
  const on = !toggleState(value, toggle);
  if (toggle === 'plan')
    return { ...value, planReview: on ? (value.planReview === 'manager' ? 'manager' : 'human') : 'off' };
  if (toggle === 'research') return { ...value, webResearch: on };
  return { ...value, executionApproval: on ? 'auto' : 'standard' };
}

/**
 * The composer's work method for the next run: three independent switches (plan first, web research,
 * Auto) on one level. Unset switches follow the agent. Auto loosens the agent, so it needs `agent:update`.
 */
export function ComposerMode({
  value,
  onChange,
  disabled = false,
}: {
  value: RunSettingsOverride;
  onChange: (value: RunSettingsOverride) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const canAuto = useCan('agent:update');
  const active = (['plan', 'research', 'auto'] as const).filter((k) => toggleState(value, k));
  const summary = active.length ? active.map((k) => t(`composerMode.${k}`)).join(' · ') : null;
  const stateLabel = `${t('composerMode.label')}: ${summary ?? t('composerMode.inherited')}`;
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={stateLabel}
          title={stateLabel}
          className="inline-flex h-7 min-w-0 items-center gap-1 rounded-md border border-border px-2 text-xs text-fg-2 hover:bg-hover focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-40"
        >
          <span className="truncate">{summary ?? t('composerMode.label')}</span>
          <span aria-hidden>⌄</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          aria-labelledby={titleId}
          className="z-50 max-h-[min(32rem,70dvh)] w-[min(22rem,calc(100vw-24px))] overflow-y-auto rounded-xl border border-border bg-panel p-3 text-sm text-fg shadow-pop"
        >
          <h2 id={titleId} className="mb-2 font-medium">
            {t('composerMode.label')}
          </h2>
          <div className="grid gap-1">
            {(['plan', 'research', 'auto'] as const).map((k) => {
              const state = toggleState(value, k);
              const locked = k === 'auto' && !canAuto && !state;
              return (
                <button
                  key={k}
                  type="button"
                  disabled={disabled || locked}
                  aria-pressed={!!state}
                  aria-label={t(`composerMode.${k}`)}
                  aria-describedby={`${titleId}-${k}`}
                  onClick={() => onChange(flipToggle(value, k))}
                  className="flex items-start gap-3 rounded-lg px-2 py-2 text-left hover:bg-hover focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-50 disabled:hover:bg-transparent"
                >
                  <span
                    aria-hidden
                    className={cn(
                      'mt-0.5 inline-flex h-4 w-7 shrink-0 items-center rounded-full p-0.5 transition-colors',
                      state ? 'bg-accent' : 'bg-border',
                    )}
                  >
                    <span
                      className={cn(
                        'size-3 rounded-full bg-panel transition-transform',
                        state && 'translate-x-3',
                      )}
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-medium">
                      {t(`composerMode.${k}`)}
                      {state === undefined ? (
                        <span className="ml-1.5 text-xs font-normal text-muted">
                          ({t('composerMode.agentDefault')})
                        </span>
                      ) : null}
                    </span>
                    <span id={`${titleId}-${k}`} className="block text-xs text-muted">
                      {t(locked ? 'composerMode.autoLocked' : `composerMode.${k}Description`)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
            <button
              type="button"
              disabled={disabled || !Object.keys(value).length}
              onClick={() => onChange({})}
              className="rounded-lg px-2 py-1 text-xs text-accent hover:bg-hover focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-40"
            >
              {t('composerMode.restore')}
            </button>
            <Popover.Close className="rounded-lg px-2 py-1 text-xs hover:bg-hover">
              {t('composerMode.close')}
            </Popover.Close>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
